// Proof-of-Work Solver — bremst automatisierte Spam-Skripte. Browser holt
// eine Challenge vom Server, sucht eine Nonce, deren SHA-256-Hash mit
// challenge|nonce N führende Null-Bits hat. Server validiert.
//
// Privacy-Story: keine externen Services, keine Tracker, keine CAPTCHAs.
// Dein Browser rechnet kurz, dann ist abgestimmt.

export type PowChallenge = { challenge: string; difficulty: number; expires_at: number };

export async function fetchPowChallenge(): Promise<PowChallenge> {
  const res = await fetch('/api/pow/challenge', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`PoW-Challenge fehlgeschlagen (HTTP ${res.status})`);
  return await res.json() as PowChallenge;
}

/** Sucht eine Nonce, sodass sha256(challenge|nonce) die geforderten führenden
 *  Null-Bits hat. Yields zurück an die UI alle paar Tausend Versuche, damit
 *  die Seite nicht einfriert. */
export async function solvePow(challenge: string, difficulty: number): Promise<string> {
  if (difficulty <= 0) return '0';
  const subtle = (typeof crypto !== 'undefined' && crypto.subtle) ? crypto.subtle : null;
  if (!subtle) {
    throw new Error('Web Crypto Subtle API not available — Slot benötigt einen modernen Browser.');
  }
  const encoder = new TextEncoder();
  let nonce = 0;
  let lastYield = performance.now();

  while (true) {
    const text = `${challenge}|${nonce}`;
    const buf = encoder.encode(text);
    const hashBuf = await subtle.digest('SHA-256', buf);
    const view = new Uint8Array(hashBuf);
    if (countLeadingZeroBits(view) >= difficulty) {
      return String(nonce);
    }
    nonce++;
    // Yield ans Event-Loop alle ~50 ms, damit UI responsive bleibt.
    if (performance.now() - lastYield > 50) {
      await new Promise<void>((r) => setTimeout(r, 0));
      lastYield = performance.now();
    }
  }
}

function countLeadingZeroBits(arr: Uint8Array): number {
  let count = 0;
  for (let i = 0; i < arr.length; i++) {
    const b = arr[i];
    if (b === 0) { count += 8; continue; }
    let mask = 0x80;
    while (mask && (b & mask) === 0) {
      count++;
      mask >>= 1;
    }
    return count;
  }
  return count;
}
